import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { TableRow, Typography } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import StatusIcon from 'src/components/StatusIcon';
import IconStatusAndColor from 'src/utils/constants/common';
import { InsuranceStatus } from 'src/utils/constants/insurance';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import { CommonActions } from 'src/utils/constants/clients';
import { useDispatch } from 'react-redux';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import {
  deleteInsuranceAction,
  updateInsurance,
  loadInsurances
} from 'src/store/actions/insuranceActions';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import InsuranceDetailDialog from 'src/views/equipment/EquipmentDetail/InsuranceDetail/IsuranceDetailDialog';
import { Link } from 'react-router-dom';

const InsuranceRow = ({
  insuranceStatus,
  remainingDays,
  policyId,
  insurer,
  agency,
  equipmentsAssociated,
  coverageStart,
  coverageEnd,
  price,
  iva,
  policyPdfUrl
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openDelete, setOpenDelete] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const handleCloseDeleteModal = () => {
    setOpenDelete(false);
  };

  const handleCloseEditModal = () => {
    setOpenEdit(false);
  };

  const handleClickMore = option => {
    if (option === CommonActions.EDIT) {
      setOpenEdit(true);
    }
    if (option === CommonActions.DELETE) {
      setOpenDelete(true);
    }
  };

  const handleDeleteModal = useCallback(() => {
    dispatch(deleteInsuranceAction(policyId));
    handleCloseDeleteModal();
  }, [policyId]);

  const handleEditInsurance = useCallback(
    values => {
      dispatch(
        updateInsurance(
          policyId,
          values,
          () => {
            dispatch(loadInsurances());
          },
          handleCloseEditModal
        )
      );
    },
    [policyId, dispatch]
  );

  const getInsuranceStatus = () => {
    const { status, message } = insuranceStatus;

    const getInsuranceDescription = messageList => {
      let description = '';
      if (messageList.length > 1) {
        messageList.forEach(msg => {
          description += `• ${t(`INSURANCES_VIEW.MESSAGE.${msg}`)}. \n `;
        });
      } else {
        description = t(`INSURANCES_VIEW.MESSAGE.${messageList[0]}`);
      }
      return description;
    };

    switch (status) {
      case InsuranceStatus.ERROR:
        return {
          description: getInsuranceDescription(message),
          type: IconStatusAndColor.ERROR.status
        };
      case InsuranceStatus.ACTIVE:
        return {
          description: t('INSURANCES_VIEW.MESSAGE.ACTIVE'),
          type: IconStatusAndColor.OK.status
        };
      default:
        return {
          description: t('COMMON.NO_INFORMATION'),
          type: IconStatusAndColor.NONE.status
        };
    }
  };

  const getInsuranceMenuOptions = () => {
    return [
      {
        name: CommonActions.EDIT,
        label: `ENUMS.ADMINISTRATIVE_ACTIONS.${CommonActions.EDIT}`,
        icon: <EditIcon />
      },
      {
        name: CommonActions.DELETE,
        label: `ENUMS.ADMINISTRATIVE_ACTIONS.${CommonActions.DELETE}`,
        icon: <DeleteIcon />
      }
    ];
  };

  const insuranceData = {
    policyId,
    insurer: insurer ? { key: insurer.id, name: insurer.name } : null,
    agency: agency ? { key: agency.id, name: agency.name } : null,
    coverageStart: coverageStart ? momentLocalTimezone(coverageStart) : null,
    coverageEnd: coverageEnd ? momentLocalTimezone(coverageEnd) : null,
    price: price || '',
    iva,
    policyPdfUrl: policyPdfUrl || null
  };

  return (
    <TableRow hover>
      <TableCell align="center">
        <StatusIcon {...getInsuranceStatus()} />
      </TableCell>
      <TableCell align="center">{remainingDays ?? 0}</TableCell>
      <TableCell align="center">
        <Typography title={policyId} align="center">
          {policyId ? (
            <Link to={`/insurances/list/${policyId}/general`}>{policyId}</Link>
          ) : (
            t('COMMON.NO_DATA')
          )}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {agency.name ?? t('COMMON.NO_INFORMATION')}
      </TableCell>
      <TableCell align="center">
        {insurer.name ?? t('COMMON.NO_INFORMATION')}
      </TableCell>
      <TableCell align="center">
        {equipmentsAssociated || t('COMMON.NO_INFORMATION')}
      </TableCell>
      <TableCell align="center">
        <MoreMenu
          options={getInsuranceMenuOptions()}
          handleClickOption={handleClickMore}
        />
        <ConfirmationDialog
          open={openDelete}
          handleClose={handleCloseDeleteModal}
          title={t('INSURANCES_VIEW.DELETE_INSURANCE')}
          description={t('INSURANCES_VIEW.DELETE_DISCLAIMER')}
          onSubmit={handleDeleteModal}
        />
        <InsuranceDetailDialog
          open={openEdit}
          handleClose={handleCloseEditModal}
          insuranceData={insuranceData}
          submit={handleEditInsurance}
        />
      </TableCell>
    </TableRow>
  );
};

InsuranceRow.propTypes = {
  id: PropTypes.number,
  remainingDays: PropTypes.number.isRequired,
  policyId: PropTypes.string.isRequired,
  insurer: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  }),
  agency: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    city: PropTypes.string
  }).isRequired,
  insuranceStatus: PropTypes.shape({
    status: PropTypes.string,
    message: PropTypes.array
  }),
  mode: PropTypes.string,
  equipmentsAssociated: PropTypes.string,
  coverageStart: PropTypes.string,
  coverageEnd: PropTypes.string,
  price: PropTypes.number,
  iva: PropTypes.number,
  policyPdfUrl: PropTypes.string
};

export default InsuranceRow;
